import { useState, useRef, useCallback, useMemo, memo } from "react";
import {
  eachDayOfInterval,
  endOfMonth,
  isAfter,
  isBefore,
  isSameDay,
  startOfDay,
  startOfMonth,
} from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { TimeSlot, BookingLanguage, FastBookingSlots } from "@/types/booking";
import type { ExternalSlot } from "@/hooks/useClinicExternalSlots";
import { de, fr, nl } from "date-fns/locale";
import CalendarSkeleton from "./CalendarSkeleton";
import { getFastBookingSlots } from "@/utils/fastBookingUtils";
import { getExternalSlotsForDate } from "@/hooks/useClinicExternalSlots";

interface CalendarComponentProps {
  currentDate: Date | undefined;
  onDateSelect: (date: Date | undefined) => void;
  fromDate: Date;
  maxDate: Date;
  language: BookingLanguage;
  selectedTimeSlots: TimeSlot[];
  isDateDisabled: (date: Date) => boolean;
  hasTimeSlotForDate: (date: Date) => boolean;
  fastBookingSlots?: FastBookingSlots;
  isLoading?: boolean; // Add optional loading prop from parent
  fullWidth?: boolean; // Allow calendar to take full width of container (for dialogs)
  externalSlots?: ExternalSlot[] | null; // External slots for showing availability indicators
  showSlotIndicators?: boolean; // Whether to show instant/request indicators on dates
  embedded?: boolean; // Whether the calendar is embedded within another component
  // Which slot-preference block this calendar belongs to. Only used to keep the
  // bookable-day test id unique: both preference blocks are mounted at once and
  // a collapsed one keeps its layout, so two calendars are on the page.
  preference?: "first" | "second";
}

// Stable style object — never changes
const CALENDAR_STYLE = {
  borderRadius: "20px",
  boxShadow: "0px 0px 30px 0px #2D2D2D0A, 0px 16px 60px 0px #2D2D2D1F",
} as const;

// Stable classNames object — never changes
const CALENDAR_CLASS_NAMES = {
  months: "flex flex-col space-y-2",
  month: "space-y-2 w-full",
  caption: "flex justify-center pt-1 relative items-center mb-0",
  caption_label: "text-lg font-normal text-neutral-900",
  nav: "space-x-1 flex items-center",
  nav_button:
    "h-8 w-8 bg-transparent border-0 rounded-full transition-opacity duration-200 flex items-center justify-center hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent",
  nav_button_previous: "absolute left-2",
  nav_button_next: "absolute right-2",
  table: "w-full border-collapse space-y-1",
  head_row: "flex mb-1 gap-1",
  head_cell:
    "text-neutral-900 rounded-md flex-1 min-w-0 h-[37px] font-normal text-sm flex items-center justify-center",
  row: "flex w-full mt-2 gap-1",
  cell: "flex-1 min-w-0 h-[37px] text-center text-[15px] p-0 relative focus-within:relative focus-within:z-20 flex items-center justify-center",
  day: "w-[37px] max-sm:w-full max-sm:max-w-[37px] h-[37px] p-0 font-normal rounded-[10px] flex items-center justify-center transition-all duration-200 border border-gray-200 text-neutral-900 text-[15px] cursor-pointer hover:bg-gray-50 relative",
  day_selected:
    "bg-light-blue-300 text-neutral-900 hover:bg-light-blue-300 hover:text-neutral-900 focus:bg-light-blue-300 focus:text-neutral-900 rounded-[10px] font-semibold border-light-blue-300 cursor-pointer",
  day_today:
    "text-neutral-900 font-normal border-gray-200 cursor-pointer hover:bg-gray-50",
  day_outside:
    "text-neutral-900 border-gray-200 cursor-pointer hover:bg-gray-50",
  day_disabled:
    "text-gray-300 opacity-40 !cursor-not-allowed hover:bg-transparent border border-gray-100",
  day_range_middle:
    "aria-selected:bg-accent aria-selected:text-accent-foreground",
  day_hidden: "invisible",
} as const;

// Stable modifiersClassNames object — never changes
const MODIFIERS_CLASS_NAMES = {
  hasFastBooking: "rdp-day_has_fast_booking",
  hasInstantSlots: "rdp-day_has_instant_slots",
  hasRequestSlots: "rdp-day_has_request_slots",
} as const;

const CalendarComponent = memo(
  ({
    currentDate,
    onDateSelect,
    fromDate,
    maxDate,
    language,
    selectedTimeSlots,
    isDateDisabled,
    hasTimeSlotForDate,
    fastBookingSlots,
    isLoading = false, // Default to false if not provided
    fullWidth = false, // Default to fixed width
    externalSlots,
    showSlotIndicators = false,
    preference,
  }: CalendarComponentProps) => {
    // Stable modifier functions — only recreated when their data dependencies change
    const hasFastBookingSlots = useCallback(
      (date: Date) => {
        if (!fastBookingSlots) return false;
        const fastSlots = getFastBookingSlots(date, fastBookingSlots);
        return fastSlots.length > 0;
      },
      [fastBookingSlots],
    );

    const hasExternalSlotsForDate = useCallback(
      (date: Date) => {
        if (!showSlotIndicators || !externalSlots) return false;
        if (isDateDisabled(date)) return false;
        const slots = getExternalSlotsForDate(externalSlots, date);
        return slots !== null && slots.length > 0;
      },
      [showSlotIndicators, externalSlots, isDateDisabled],
    );

    const hasOnlyRequestSlots = useCallback(
      (date: Date) => {
        if (!showSlotIndicators) return false;
        if (isDateDisabled(date)) return false;
        return !hasExternalSlotsForDate(date);
      },
      [showSlotIndicators, isDateDisabled, hasExternalSlotsForDate],
    );

    // Stable modifiers object — only recreated when modifier functions change
    const modifiers = useMemo(
      () => ({
        hasFastBooking: hasFastBookingSlots,
        hasInstantSlots: hasExternalSlotsForDate,
        hasRequestSlots: hasOnlyRequestSlots,
      }),
      [hasFastBookingSlots, hasExternalSlotsForDate, hasOnlyRequestSlots],
    );

    // Track the displayed month — initialized from currentDate, updated by user navigation
    const [displayedMonth, setDisplayedMonth] = useState<Date>(
      () => currentDate || fromDate,
    );

    // Only sync displayed month when currentDate itself changes (not on every render)
    // This preserves manual arrow navigation while following programmatic date changes
    const prevCurrentDateRef = useRef(currentDate);
    if (prevCurrentDateRef.current !== currentDate) {
      prevCurrentDateRef.current = currentDate;
      if (currentDate) {
        setDisplayedMonth(currentDate);
      }
    }

    // The earliest bookable day of the month on screen is the one a browser
    // test means when it picks a date, and it is the only stable name such a
    // test can record: the day NUMBER moves with availability (EAGNT-744).
    const dayTestId = useMemo(() => {
      const firstAvailable = eachDayOfInterval({
        start: startOfMonth(displayedMonth),
        end: endOfMonth(displayedMonth),
      }).find(
        (date) =>
          !isBefore(date, startOfDay(fromDate)) &&
          !isAfter(date, maxDate) &&
          !isDateDisabled(date),
      );
      if (!firstAvailable) return undefined;
      const testId = `earliest-bookable-day-${preference ?? "only"}`;
      return (date: Date) =>
        isSameDay(date, firstAvailable) ? testId : undefined;
    }, [displayedMonth, fromDate, maxDate, isDateDisabled, preference]);

    // Don't show disabled dates as selected in the calendar
    // (but keep them in parent state for time slot display)
    const displayDate =
      currentDate && !isDateDisabled(currentDate) ? currentDate : undefined;

    // Stable select handler
    const handleDateSelect = useCallback(
      (date: Date | undefined) => {
        if (!date && currentDate && !isDateDisabled(currentDate)) {
          console.log(
            "CalendarComponent: Prevented manual deselection, keeping current date",
          );
          return;
        }
        onDateSelect(date);
      },
      [currentDate, isDateDisabled, onDateSelect],
    );

    const className = `calendar-container ${fullWidth ? "w-full lg:w-[344px]" : "w-[344px] max-sm:w-full max-sm:max-w-[344px]"} min-h-[340px] h-auto mx-auto bg-white border border-gray-100 pt-[16px] px-[16px] pb-[12px]`;

    const locale =
      language === "de-DE"
        ? de
        : language === "fr-FR"
          ? fr
          : language === "nl-NL"
            ? nl
            : undefined;

    return (
      // The month nav arrows are react-day-picker's own buttons: same markup
      // and the same "Go to next month" accessible name in every calendar on
      // the page, and the date step mounts one per slot-preference block. A
      // recorded browser test therefore has nothing to tell them apart, and
      // the recorder refuses a step it cannot re-find (EAGNT-744) — the same
      // gap the bookable-day testid closed for the day cells. v8 gives no way
      // to put an attribute on those buttons, so the anchor goes on the
      // container instead: the recorder scopes an element's xpath to its
      // nearest `data-testid` ancestor, which makes every control inside this
      // calendar unambiguous without touching react-day-picker's rendering.
      <div
        className="justify-center"
        data-testid={`calendar-${preference ?? "only"}`}
      >
        <style>{`
        .calendar-container [aria-disabled="true"] {
          cursor: not-allowed !important;
        }

        .calendar-container {
          max-width: 100%;
          border-radius: 20px !important;
          box-shadow: none !important;
        }

        .calendar-container button[disabled] {
          cursor: not-allowed !important;
        }
        .calendar-container .rdp-day_disabled {
          cursor: not-allowed !important;
        }
        .calendar-container .rdp-nav_button svg {
          width: 20px !important;
          height: 20px !important;
          color: #2D2D2D !important;
        }
        /* Instant slots indicator - green dot (on enabled dates, including selected) */
        .calendar-container .rdp-day_has_instant_slots:not([aria-disabled="true"]):not(.rdp-day_disabled)::after {
          content: '';
          position: absolute;
          bottom: 3px;
          left: 50%;
          transform: translateX(-50%);
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background-color: #57B262;
        }
        /* Request slots indicator - gray dot (on enabled dates without external slots, including selected) */
        .calendar-container .rdp-day_has_request_slots:not([aria-disabled="true"]):not(.rdp-day_disabled)::after {
          content: '';
          position: absolute;
          bottom: 3px;
          left: 50%;
          transform: translateX(-50%);
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background-color: #B5B5B5;
        }
      `}</style>
        <Calendar
          mode="single"
          selected={displayDate}
          month={displayedMonth}
          onMonthChange={setDisplayedMonth}
          onSelect={handleDateSelect}
          disabled={isDateDisabled}
          fromDate={fromDate}
          toDate={maxDate}
          showOutsideDays={false}
          className={className}
          style={CALENDAR_STYLE}
          locale={locale}
          weekStartsOn={1}
          modifiers={modifiers}
          classNames={CALENDAR_CLASS_NAMES}
          modifiersClassNames={MODIFIERS_CLASS_NAMES}
          dayTestId={dayTestId}
        />
      </div>
    );
  },
);

CalendarComponent.displayName = "CalendarComponent";

export default CalendarComponent;
